<template>
  <page layoutClass="jobs-edit" :loading="$apollo.loading" :err="err">
    <b-row>
      <b-col col lg="8" offset-lg="2">
        <b-card>

          <b-row class="pb-3 mb-3 border-bottom">
            <b-col v-if="(job && job.project) || projectID" cols="12" class="pb-2 block-title">
              {{ isNew ? 'New Role' : 'Edit Role' }}
            </b-col>
            <b-col v-else cols="12" class="pb-2 block-title">
              {{ isNew ? 'New Job' : 'Edit Job' }}
            </b-col>
            <b-col v-if="(job && job.project) || projectID" cols="12" class="pb-2">
              <router-link :to="{ name: 'ProjectShow', params: { projectID:  (job && job.project) ? job.project.id : projectID } }">
                &laquo; Back to project
              </router-link>
              <template v-if="!isNew && job">
                <br>
                <router-link :to="{ name: 'InternalJobShow', params: { jobID:  job.id } }">
                  &laquo; Back to role
                </router-link>
              </template>
            </b-col>
            <b-col v-else cols="12" class="pb-2">
              <router-link
                v-if="!isNew && (job && job.isInternal || !isReferralsFeatureEnabled)"
                :to="{ name: 'InternalJobShow', params: { jobID:  job.id } }"
              >
                &laquo; Back to job
              </router-link>
              <router-link
                v-else-if="isNew && !isReferralsFeatureEnabled"
                :to="{ name: 'CampaignJobIndex' }"
              >
                &laquo; Back to jobs
              </router-link>
              <router-link v-else :to="{ name: 'CampaignJobIndex' }">
                &laquo; Back to jobs
              </router-link>
            </b-col>
          </b-row>

          <b-form autocomplete="off" method="POST" @submit.prevent="() => isNew ? createJob() : updateJob()">

            <b-form-group label="Title" label-for="formTitle">
              <b-form-input id="formTitle" type="text" required placeholder="Title" v-model="form.title" />
            </b-form-group>

            <b-form-group label="Description" label-for="formDescription">
              <markdown-editor :configs="markdownConfig" id="formDescription" placeholder="Description" v-model="form.description"/>
            </b-form-group>

            <job-skills-selector
              v-if="isInternalMobilityEnabled"
              :options="null"
              :settings="{ noHeader: true }"
              :isFreeText="true"
              :value="form.skills"
              name="Required Skills"
              @input="skillsInput($event, 'REQUIRED_SKILLS')"
            />

            <job-skills-selector
              v-if="isInternalMobilityEnabled"
              :options="null"
              :settings="{ noHeader: true }"
              :isFreeText="true"
              :value="form.nonRequiredSkills"
              name="Optional Skills"
              @input="skillsInput($event, 'NON_REQUIRED_SKILLS')"
            />

            <b-form-group
              label="Development role"
              label-for="is-development-role"
              v-if="(job && job.project) || projectID"
            >
              <b-form-checkbox id="is-development-role" v-model="form.isDevelopment">
                This is a development role
              </b-form-checkbox>
            </b-form-group>
            <b-form-group
              v-if="isReferralsFeatureEnabled && isInternalMobilityEnabled"
              label="Job Type"
              label-for="is-internal-job"
            >
              <b-form-select
                v-model="form.isInternal"
                :options="[
                  { value: null, text: 'Internal and referral' },
                  { value: true, text: 'Internal only' },
                  { value: false, text: 'Referral only' },
                ]"
                :disabled="((job && job.project) || projectID) && true"
              ></b-form-select>
            </b-form-group>

            <b-form-group label="Applicant Tracking System" v-if="job && isRecruiterAdmin">
              <dl class="row">
                <dt class="col-sm-3">ATS ID</dt>
                <dd class="col-sm-9" v-if="job.atsID">{{ job.atsID }}</dd>
                <dd class="col-sm-9 font-italic" v-else>None</dd>

                <dt class="col-sm-3">ATS Data</dt>
                <dd class="col-sm-9" v-if="job && job.atsData">
                  <dl class="border row m-0">
                    <dt class="col-4 border-right py-1">Key</dt>
                    <dd class="col-8 m-0 py-1 font-weight-bold">Value</dd>
                    <template v-for="(dataValue, dataKey) in job.atsData">
                      <dt class="col-4 border-top border-right py-1" :key="`${dataKey}-label`">{{ dataKey | _startCase }}</dt>
                      <dd class="col-8 m-0 py-1 border-top text-truncate" :key="`${dataKey}-value`" :title="dataValue">{{ dataValue || '-' }}</dd>
                    </template>
                  </dl>
                </dd>
                <dd class="col-sm-9 font-italic" v-else>None</dd>

                <dt class="col-sm-3">ATS URL</dt>
                <dd class="col-sm-9" v-if="job.atsURL"><a :href="job.atsURL" rel="external nofollow noreferer">{{ job.atsURL.replace(/http(s)?:\/\/(www.)?/, '') }}</a></dd>
                <dd class="col-sm-9 font-italic" v-else>None</dd>
              </dl>
            </b-form-group>

            <b-form-group label="Department" label-cols-md="3" v-if="isReferralsFeatureEnabled">
              <b-form-select :options="departmentsList" v-model="form.department"/>
            </b-form-group>

            <b-row v-if="(job && job.project) || projectID">
              <b-col>
                <b-form-group label="Start Date">
                  <date-picker
                    v-model="form.startDate"
                    type="day"
                    lang="en"
                    format="DD MMM YYYY"
                  />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Estimated End Date">
                  <date-picker
                    v-model="form.endDate"
                    type="day"
                    lang="en"
                    format="DD MMM YYYY"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <job-business-area-selector
              v-if="isInternalMobilityEnabled"
              id="business-area"
              :options="businessAreaOptions"
              v-model="form.businessArea"
            />

            <job-seniority-selector v-if="isInternalMobilityEnabled" id="seniority" v-model="form.seniority" />

            <b-form-group label="Location" :label-cols-md="isInternalMobilityEnabled ? 12 : 3">
              <location-search id="location" v-model="form.location"/>
            </b-form-group>

            <b-form-group label="Industry" label-cols-md="3" v-if="isReferralsFeatureEnabled">
              <multiselect v-bind="multiselectConfig" v-model="form.industry" :options="industriesList"
                @tag="value => this.form.industry = value"/>
            </b-form-group>

            <b-form-group label="Bonus Points Multiplier" label-cols-md="3" v-if="isReferralsFeatureEnabled">
              <b-form-input type="number" step="0.1" v-model="form.pointsMultiplier"/>
            </b-form-group>

            <b-form-group label="Referral Bonus" label-cols-md="3" label-for="referralBonus" v-if="isReferralsFeatureEnabled">
              <b-form-input id="referralBonus" type="text" v-model="form.referralBonus"/>
            </b-form-group>

            <b-form-group label-for="isPinned" label-cols-md="3" v-if="!isInternalMobilityEnabled">
              <b-form-checkbox id="isPinned" v-model="form.isPinned">Pin this job to the top of results</b-form-checkbox>
              <b-form-text>This will affect the Dashboard, Job Listing pages &amp; Emails</b-form-text>
            </b-form-group>

            <advanced-form autocomplete="off" layout="inline-md-3" tag="div" hide-form-control
              :form="customFieldsForm" v-model="customFields" v-if="!isInternalMobilityEnabled">
                <template v-slot:linkedInSearchURL-helpText v-if="job && job.linkedInSearchURL">
                  <b-form-text>Override the default LinkedIn Search URL for this job.</b-form-text>
                  <b-form-text v-if="job && job.linkedInSearchURL">To start editing, <a :href="job.linkedInSearchURL" rel="external nofollow noreferrer" target="_blank">open the default LinkedIn search URL</a>.</b-form-text>
                </template>
                <template v-slot:linkedInSearchURL-helpText v-else>
                  <b-form-text>Set a LinkedIn Search URL for this job.</b-form-text>
                  <b-form-text>Save a draft of this job to generate a default LinkedIn search.</b-form-text>
                </template>
            </advanced-form>

            <b-form-group label="Status" :label-cols-md="isInternalMobilityEnabled ? 12 : 3">
              <b-form-select :options="statusList" v-model="form.status"/>
            </b-form-group>

            <b-button v-if="isNew" type="submit" variant="primary">Create Job</b-button>
            <b-button v-else type="submit" variant="primary">Save Changes</b-button>

          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </page>
</template>

<script>
import _startCase from 'lodash/startCase';
import gql from 'graphql-tag';
import AdvancedForm from '@src/components/AdvancedForm';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import Multiselect from 'vue-multiselect';
import { markdownEditor } from 'vue-simplemde';
import Page from '@/components/Page';
import LocationSearch, { GWROptions } from '@/components/misc/LocationSearch';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import JobBusinessAreaSelector from '@/components/profile/JobBusinessAreaSelector';
import JobSenioritySelector from '@/components/profile/JobSenioritySelector';
import DatePicker from 'vue2-datepicker';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import { parseIfPossible } from '@src/lib/imMatching';

const EditJobQuery = gql`
  ${JobDetailsFragment}

  query JobEdit($jobID: ID) {
    company {
      id

      job(where: { id: $jobID }) {
        ...JobDetails
        atsData
        customFieldsHash
        skills
        nonRequiredSkills
        project {
          id
          title
        }
      }

      departments {
        id
        name
        icon
      }

      jobAggs {
        industries
      }
    }
  }
`;

const NewJobMutation = gql`
  ${JobDetailsFragment}

  mutation JobNew($create: CreateJobInput!) {
    createJob(create: $create) {
      ...JobDetails
    }
  }
`;

const AddSkillsTaxonomyMutation = gql`
  mutation AddSkillsTaxonomy($create: CreateTaxonomySkillInput!) {
    createTaxonomySkills(create: $create)
  }
`;

const EditJobMutation = gql`
  ${JobDetailsFragment}

  mutation JobEdit($jobID: ID!, $update: UpdateJobInput!) {
    updateJob(update: $update, where: { id: $jobID }) {
      ...JobDetails
    }
  }
`;

export default {
  props: {
    jobID: String,
    projectID: String,
  },
  components: {
    AdvancedForm,
    markdownEditor,
    Multiselect,
    Page,
    LocationSearch,
    JobSkillsSelector,
    JobBusinessAreaSelector,
    JobSenioritySelector,
    DatePicker,
  },
  data() {
    const { startDate, endDate } = this.$route.query || {};
    return {
      err: null,
      form: {
        companyName: null,
        title: null,
        description: null,
        skills: [],
        nonRequiredSkills: [],
        department: null,
        location: null,
        industry: null,
        referralBonus: null,
        pointsMultiplier: null,
        atsID: null,
        atsURL: null,
        status: 'ACTIVE',
        isDevelopment: false,
        isPinned: false,
        seniority: null,
        businessArea: null,
        isInternal: this.projectID ? true : null,
        project: this.projectID ? { id: this.projectID } : null,
        startDate: startDate ? parseInt(startDate) : null,
        endDate: endDate ? parseInt(endDate) : null,
      },
      departmentsList: [],
      locationsList: [],
      industriesList: [],
      newSkills: {
        REQUIRED_SKILLS: [],
        NON_REQUIRED_SKILLS: [],
      },
      customFields: {},
    };
  },
  apollo: {
    job: {
      query: EditJobQuery,
      variables() {
        return this.isNew ? null : { jobID: this.jobID };
      },
      fetchPolicy: 'network-only',
      update: ({ company: { job } }) => job,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company } = data;

          if (company.job) {
            this.setFormData();
            this.customFields = { ...company.job.customFieldsHash };
          }

          if (company.departments) {
            this.departmentsList = company.departments.map(({ id, name }) => ({ text: name, value: id }));
          }

          if (company.jobAggs) {
            const { industries } = company.jobAggs;
            this.industriesList = industries;
          }
        }
      },
      error(err) {
        this.err = `${err}`;
      },
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? 'New Job' : 'Edit Job',
    };
  },
  filters: {
    _startCase: value => _startCase(value),
  },
  computed: {
    ...mapRoot([ 'company', 'isReferralsFeatureEnabled', 'isInternalMobilityEnabled', 'isRecruiterAdmin' ]),
    ...mapPreferences([ 'enableSingleUseLinks' ]),
    isNew() {
      return !this.jobID;
    },
    markdownConfig: () => ({
      // autoDownloadFontAwesome: false,
      forceSync: true,
      indentWithTabs: false,
      renderingConfig: { codeSyntaxHighlighting: false },
      promptURLs: true,
      spellChecker: true,
      status: false,
      toolbar: [ 'bold', 'italic', '|', 'link', 'horizontal-rule' ],
    }),
    multiselectConfig: () => ({
      'class': 'multiselect-rlnx',
      'placeholder': 'Type to search',
      'max-height': 600,
      'open-direction': 'bottom',
      'searchable': true,
      'show-no-results': false,
      'taggable': true,
      'tag-position': 'bottom',
      'tag-placeholder': 'Press enter to select',
      'close-on-select': true,
    }),
    statusList: () => ({
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'DELETED': 'Deleted',
    }),
    customFieldsForm() {
      const { isReferralsFeatureEnabled, isInternalMobilityEnabled, enableSingleUseLinks } = this;
      return {
        location: {
          type: 'STRING',
          label: 'Location',
          helpText: 'Set custom text for the location',
        },
        ...(isReferralsFeatureEnabled ? {
          linkedInSearchURL: {
            type: 'STRING',
            label: 'LinkedIn Search URL',
            placeholder: 'https://linkedin.com/search/people ...',
          },
          jobShareText: {
            type: 'TEXTAREA',
            label: 'Job Share Example Text',
            placeholder: 'Apply today and work with me as ...',
          },
          applicationFormURL: {
            type: 'STRING',
            label: 'Apply Form URL',
            helpText: 'Specify an external website where a candidate should apply for this job. Candidates will be immediately redirected to here - unless Apply Then Redirect is checked below. We will count page views as redirects instead.',
          },
          applyThenRedirect: {
            type: 'CHECKBOX',
            description: 'Apply Then Redirect',
            helpText: 'If checked, a candidate will only be redirected to the Apply Form URL after submitting the form on Real Links.',
          },
          ...(enableSingleUseLinks ? {
            singleUseLink: {
              type: 'CHECKBOX',
              description: 'Limit Applications per Share',
              helpText: 'If checked, only a single candidate may apply via each share link.',
            },
          } : {}),
          recruiterEmail: {
            type: 'STRING',
            label: 'Recruiter Email',
            helpText: 'Comma-separated emails to receive applications for this role - unused if Apply Form URL is given & Apply Then Redirect is unchecked.',
          },
        } : {}),
        ...(isInternalMobilityEnabled ? {
          businessArea: {
            type: 'STRING',
            label: 'Business Area',
            helpText: 'Set custom text for the business area',
          },
          applicationFormURL: {
            type: 'STRING',
            label: 'Apply Form URL',
            helpText: 'Specify an external website where a candidate should apply for this job. Candidates will be redirected to the Apply Form URL when they click the Apply button.',
          },
        } : {}),
      };
    },
    businessAreaOptions() {
      const { customFields } = this.company || {};
      const field = (Array.isArray(customFields) ? customFields : [])
        .find(({ type }) => type === 'JOB_BUSINESS_AREA');
      const options = field && field.options ? parseIfPossible(field.options) : null;
      if (Array.isArray(options) && options.length) {
        return options;
      }
      return null;
    },
  },
  methods: {
    getFormData() {
      const data = {};

      Object.keys(this.form).forEach(prop => {
        if (this.$root.isGWR && prop === 'location') {
          data.customFieldsHash = data.customFieldsHash || {};
          data.customFieldsHash.location = this.form[prop] ? this.form[prop].name : null;
        } else if ([ 'department', 'location' ].includes(prop)) {
          data[prop] = this.form[prop] && this.form[prop].id ? { id: this.form[prop].id } : null;
        } else if ([ 'companyName', 'seniority', 'startDate', 'endDate', 'businessArea' ].includes(prop)) {
          data.customFieldsHash = data.customFieldsHash || {};
          data.customFieldsHash[prop] = this.form[prop];
        } else if ([ 'pointsMultiplier' ].includes(prop)) {
          data[prop] = parseFloat(this.form[prop]);
        } else if (typeof this.form[prop] === 'string') {
          // Empty strings should be turned into `NULL`
          data[prop] = this.form[prop].length ? this.form[prop] : null;
        } else {
          data[prop] = this.form[prop];
        }
      });

      data.customFieldsHash = { ...data.customFieldsHash, ...this.customFields };

      if (!data.status) {
        data.status = 'INACTIVE';
      }

      return data;
    },
    setFormData() {
      Object.keys(this.form).forEach(this.job ? prop => {
        if (prop === 'department') {
          this.form[prop] = this.job[prop] && this.job[prop].id ? this.job[prop].id : null;
        } else if (prop === 'project') {
          this.form[prop] = this.job[prop] && this.job[prop].id ? { id: this.job[prop].id } : null;
        } else if ([ 'companyName', 'seniority', 'startDate', 'endDate', 'businessArea' ].includes(prop)) {
          this.form[prop] = Array.isArray(this.job.customFields)
            ? (this.job.customFields.find(({ key }) => key === prop) || {}).value
            : null;
        } else if (prop === 'isInternal' && !this.isReferralsFeatureEnabled) {
          this.form[prop] = true;
        } else if (this.$root.isGWR && prop === 'location') {
          const locationName = Array.isArray(this.job.customFields)
            ? (this.job.customFields.find(({ key }) => key === prop) || {}).value
            : null;
          const selectedLocation = GWROptions.find(({ name }) => name === locationName);
          this.form[prop] = selectedLocation;
        } else {
          this.form[prop] = this.job[prop];
        }
      } : prop => this.form[prop] = null);
    },
    async createJob() {
      try {
        this.$assert(this.isNew, new Error('Cannot createJob with updateJob params'));

        const create = this.getFormData();
        this.$log.debug({ create });

        const { data: { createJob: { id: jobID } = {} } = {} } = await this.$apollo.mutate({
          mutation: NewJobMutation,
          variables: { create },
        });

        this.$assert(jobID, new Error('Expected createJob to return a jobID'));

        const { isInternalMobilityEnabled, newSkills } = this;
        const additionalSkills = Object.keys(newSkills)
          .reduce((out, key) => [ ...new Set([ ...out, ...newSkills[key] ]) ], []);
        if (isInternalMobilityEnabled && Array.isArray(additionalSkills) && additionalSkills.length) {
          await this.$apollo.mutate({
            mutation: AddSkillsTaxonomyMutation,
            variables: {
              create: {
                jobTitle: create.title,
                skills: additionalSkills
              }
            },
          });
        }

        if (this.form.isInternal || isInternalMobilityEnabled) {
          this.$router.replace({ name: 'InternalJobShow', params: { jobID } });
        } else {
          this.$router.replace({ name: 'JobShow', params: { jobID } });
        }
      } catch (err) {
        this.$log.error(err);
        this.err = `${err}`;
      }
    },
    async updateJob() {
      try {
        this.$assert(!this.isNew, new Error('Cannot createJob with updateJob params'));

        const update = this.getFormData();
        this.$log.debug({ update });

        await this.$apollo.mutate({
          mutation: EditJobMutation,
          variables: { jobID: this.jobID, update },
        });

        const { isInternalMobilityEnabled, newSkills } = this;
        const additionalSkills = Object.keys(newSkills)
          .reduce((out, key) => [ ...new Set([ ...out, ...newSkills[key] ]) ], []);
        if (isInternalMobilityEnabled && Array.isArray(additionalSkills) && additionalSkills.length) {
          await this.$apollo.mutate({
            mutation: AddSkillsTaxonomyMutation,
            variables: {
              create: {
                jobTitle: update.title,
                skills: additionalSkills
              }
            },
          });
        }

        if (this.form.isInternal && isInternalMobilityEnabled) {
          this.$router.replace({ name: 'InternalJobShow', params: { jobID: this.jobID } });
        } else {
          this.$router.replace({ name: 'JobShow', params: { jobID: this.jobID } });
        }
      } catch (err) {
        this.$log.error(err);
        this.err = `${err}`;
      }
    },
    skillsInput(input, key) {
      if (key === 'REQUIRED_SKILLS') {
        this.form.skills = input.input;
      } else if (key === 'NON_REQUIRED_SKILLS') {
        this.form.nonRequiredSkills = input.input;
      }
      this.newSkills[key] = input.newTerms;
      console.log('NEW SKILLS FROM INPUT: ', this.newSkills);
    },
  },
};
</script>

<style lang="scss">
.page .jobs-edit {

  .markdown-editor .CodeMirror {
    height: 300px;

    .CodeMirror-lines {
      padding-bottom: 20px;
    }
  }

  .block-title {
    text-transform: uppercase;
  }

}
</style>



// WEBPACK FOOTER //
// src/views/jobs/jobEdit.vue